import { useEffect, useMemo, useRef, useState } from 'react'
import EnterpriseIcon, {
  ChevronDownIcon,
  SearchIcon,
} from '@enterprise-ui/icons'
import { autocompleteCustomSearch } from '../../../common/helper'
import {
  AccountLabel,
  ChevronIcon,
  DropdownButton,
  DropdownContainer,
  DropdownItem,
  DropdownMenu,
  NoResults,
  SearchContainer,
  SearchInput,
} from './styles/AccountDropdown'

interface Props {
  selectedAccount: any
  accounts: any[]
  onAccountChange: (account: any) => void
  isSwitchAccountVisible: boolean
  setSwitchAccountVisible: (visible: boolean) => void
  isExternalUser: boolean
  isLoading: number
  children?: React.ReactNode
}

const AccountDropdown = ({
  selectedAccount,
  accounts,
  onAccountChange,
  isSwitchAccountVisible,
  setSwitchAccountVisible,
  isExternalUser,
  isLoading,
  children,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  // Close the dropdown when clicking outside of it
  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // Close the dropdown when the viewport crosses the mobile breakpoint
  // (matches the `max-width: 900px` media query in the header styles).
  // A resize is not a click, so the click-outside handler above won't fire
  // and the dropdown would otherwise stay open when the layout switches.
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 900px)')
    const handleViewportChange = () => setIsOpen(false)
    mediaQuery.addEventListener('change', handleViewportChange)
    return () => mediaQuery.removeEventListener('change', handleViewportChange)
  }, [])

  const handleAccountSelect = (account: any) => {
    onAccountChange(account)
    setIsOpen(false)
    setSearchText('')
  }

  const getAccountLabel = (account: any) =>
    account?.displayName || account?.name || account?.label || ''

  const displayName = getAccountLabel(selectedAccount) || 'Select Account'

  // Filter accounts using the shared autocomplete search helper
  const filteredAccounts = useMemo(() => {
    if (!searchText.trim()) return accounts || []
    return (accounts || []).filter((account: any) =>
      autocompleteCustomSearch(searchText, { label: getAccountLabel(account) }),
    )
  }, [accounts, searchText])

  return (
    <DropdownContainer ref={containerRef}>
      <DropdownButton
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <AccountLabel>{displayName}</AccountLabel>
        <ChevronIcon icon={ChevronDownIcon} $isOpen={isOpen} />
      </DropdownButton>

      <DropdownMenu isOpen={isOpen} role="listbox">
        <SearchContainer>
          <EnterpriseIcon icon={SearchIcon} size="sm" />
          <SearchInput
            type="text"
            placeholder="Search accounts"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            aria-label="Search accounts"
          />
        </SearchContainer>
        {filteredAccounts.length > 0 ? (
          filteredAccounts.map((account: any) => (
            <DropdownItem
              key={account.id}
              isSelected={selectedAccount?.id === account.id}
              onClick={() => handleAccountSelect(account)}
            >
              {getAccountLabel(account)}
            </DropdownItem>
          ))
        ) : (
          <NoResults>No accounts found</NoResults>
        )}
      </DropdownMenu>

      {/* Render account selection modal and vendor details as children */}
      {children}
    </DropdownContainer>
  )
}

export default AccountDropdown
